import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { InteractionCue } from './ProjectScrapbooks'

const artifacts = {
  brief: {
    eyebrow: 'BRIEF / 01',
    label: '先把问题问对',
    text: '我会先确认产品事实、库存与重点门店，再追问：这次传播要改变哪一个行为？如果答案只是“制造声量”，策略就还没有被定义。',
    output: '交付物：一页式策略 Brief ＋ 待确认问题清单',
  },
  window: {
    eyebrow: 'INSIGHT / 02',
    label: '夜归时刻证据卡',
    text: '洞察不是一句漂亮情绪，而是“白天持续回应他人，回家后才恢复控制权”这一可观察张力；访谈会继续验证 20:00 是否足够共有。',
    output: '交付物：人群张力卡 ＋ 洞察证据来源',
  },
  clock: {
    eyebrow: 'STRATEGY / 03',
    label: '20:00 是策略总开关',
    text: '同一个时间点同时约束内容发布时间、灯箱切换、门店夜场与产品使用场景，让每个触点都在回答同一个问题。',
    output: '交付物：策略主线 ＋ 触点一致性检查表',
  },
  phone: {
    eyebrow: 'CONTENT / 04',
    label: '15 秒主片怎么拍',
    text: '0–5 秒：关掉工作通知；6–11 秒：脱鞋、开窗、喷香；12–15 秒：只留“今晚不再回复世界”与产品。每个镜头都服务于下班切换。',
    output: '交付物：分镜脚本 ＋ 七夜选题表 ＋ 创作者 Brief',
  },
  scent: {
    eyebrow: 'PRODUCT / 05',
    label: '情绪必须接回产品',
    text: '所有传播触点都至少出现一次真实使用动作，并把“晚八点”写进试香卡与店员话术，避免用户只记得文案、不记得限定香气。',
    output: '交付物：产品信息优先级 ＋ 试香卡文案',
  },
  message: {
    eyebrow: 'RETAIL / 06',
    label: '气味留言处 SOP',
    text: '到店后先试闻，再写下今晚想放下的事，投入信箱后领取晚安试香纸；每一步都明确物料、店员动作和需要记录的参与信号。',
    output: '交付物：门店动线 ＋ 店员话术 ＋ 数据记录表',
  },
} as const

type NoteKey = keyof typeof artifacts

const artifactKeys = Object.keys(artifacts) as NoteKey[]

export default function AfterEightScrapbook() {
  const [active, setActive] = useState<NoteKey>('brief')
  const [visited, setVisited] = useState<NoteKey[]>(['brief'])
  const [isNight, setIsNight] = useState(true)
  const [phoneOffline, setPhoneOffline] = useState(true)
  const [envelopeOpen, setEnvelopeOpen] = useState(false)
  const [sprayPulse, setSprayPulse] = useState(0)
  const [ticketPosition, setTicketPosition] = useState({ x: 0, y: 0 })
  const drag = useRef<{ pointerX: number; pointerY: number; x: number; y: number } | null>(null)

  const activate = (key: NoteKey) => {
    setActive(key)
    setVisited((current) => current.includes(key) ? current : [...current, key])
  }

  const startDrag = (event: PointerEvent<HTMLButtonElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      x: ticketPosition.x,
      y: ticketPosition.y,
    }
  }

  const moveDrag = (event: PointerEvent<HTMLButtonElement>) => {
    if (!drag.current) return
    const nextX = Math.max(-70, Math.min(90, drag.current.x + event.clientX - drag.current.pointerX))
    const nextY = Math.max(-80, Math.min(100, drag.current.y + event.clientY - drag.current.pointerY))
    setTicketPosition({ x: nextX, y: nextY })
  }

  const moveWithKeyboard = (event: KeyboardEvent<HTMLButtonElement>) => {
    const direction = {
      ArrowLeft: [-8, 0],
      ArrowRight: [8, 0],
      ArrowUp: [0, -8],
      ArrowDown: [0, 8],
    }[event.key]
    if (!direction) return
    event.preventDefault()
    setTicketPosition(({ x, y }) => ({
      x: Math.max(-70, Math.min(90, x + direction[0])),
      y: Math.max(-80, Math.min(100, y + direction[1])),
    }))
  }

  const completed = visited.length === artifactKeys.length

  return (
    <div
      className={`scrapbook-board ${isNight ? 'is-night' : ''} ${envelopeOpen ? 'is-envelope-open' : ''}`}
      role="group"
      aria-label="晚八点以后营销策划互动工作台"
    >
      <div className="scrapbook-paper scrapbook-paper--blue" aria-hidden="true" />
      <div className="scrapbook-paper scrapbook-paper--red" aria-hidden="true" />

      <div className="scrapbook-progress" aria-live="polite">
        <span>策划底稿探索</span>
        <strong>{visited.length} / {artifactKeys.length}</strong>
        <div aria-hidden="true">
          {artifactKeys.map((key) => <i key={key} className={visited.includes(key) ? 'is-stamped' : ''} />)}
        </div>
      </div>
      {!completed && <InteractionCue label="来互动一下吧" />}

      <button type="button" className={`scrapbook-brief ${active === 'brief' ? 'is-active' : ''}`} onClick={() => activate('brief')}>
        <span className="interaction-index">01</span>
        <small>CLIENT BRIEF</small>
        <strong>夏末限定，<br />到底要解决什么？</strong>
        <span>翻开 →</span>
      </button>

      <button
        type="button"
        className={`scrapbook-window ${active === 'window' ? 'is-active' : ''}`}
        aria-label={isNight ? '查看白天状态与用户洞察' : '查看夜晚状态与用户洞察'}
        aria-pressed={isNight}
        onClick={() => { setIsNight((value) => !value); activate('window') }}
      >
        <span className="interaction-index">02</span>
        <span className="scrapbook-moon" aria-hidden="true" />
        <span className="scrapbook-sun" aria-hidden="true" />
        <span className="city city--one" aria-hidden="true" />
        <span className="city city--two" aria-hidden="true" />
        <span className="city city--three" aria-hidden="true" />
        <span className="curtain curtain--left" aria-hidden="true" />
        <span className="curtain curtain--right" aria-hidden="true" />
        <small>{isNight ? '下班后的我' : '工作中的我'}</small>
      </button>

      <button type="button" className={`scrapbook-clock ${active === 'clock' ? 'is-active' : ''}`} onClick={() => activate('clock')}>
        <span className="interaction-index">03</span>
        <span className="clock-hand clock-hand--hour" aria-hidden="true" />
        <span className="clock-hand clock-hand--minute" aria-hidden="true" />
        <strong>20:00</strong>
      </button>

      <button
        type="button"
        className={`scrapbook-perfume ${active === 'scent' ? 'is-active' : ''}`}
        onClick={() => { setSprayPulse((value) => value + 1); activate('scent') }}
      >
        <span className="interaction-index">05</span>
        <span aria-hidden="true" />
        <strong>喷一下</strong>
        <i key={sprayPulse} className={sprayPulse ? 'spray-is-active' : ''} aria-hidden="true" />
      </button>

      <button
        type="button"
        className={`scrapbook-phone ${active === 'phone' ? 'is-active' : ''} ${phoneOffline ? 'is-offline' : ''}`}
        aria-pressed={phoneOffline}
        onClick={() => { setPhoneOffline((value) => !value); activate('phone') }}
      >
        <span className="interaction-index">04</span>
        <span aria-hidden="true">{phoneOffline ? '☾' : '•••'}</span>
        <strong>{phoneOffline ? 'OFFLINE' : '3 NEW'}</strong>
        <small>点按切换</small>
      </button>

      <button type="button" className={`scrapbook-message ${active === 'message' ? 'is-active' : ''}`} onClick={() => { setEnvelopeOpen((value) => !value); activate('message') }}>
        <span className="interaction-index">06</span>
        <span aria-hidden="true" />
        <strong>{envelopeOpen ? '留言已投入 ✓' : '今晚想放下的事'}</strong>
        <small>{envelopeOpen ? '再点一次合上' : '点开信封'}</small>
      </button>

      <button
        type="button"
        className="draggable-ticket"
        aria-label="可拖动的策略贴纸；也可以使用方向键移动"
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={() => { drag.current = null }}
        onPointerCancel={() => { drag.current = null }}
        onLostPointerCapture={() => { drag.current = null }}
        onKeyDown={moveWithKeyboard}
        style={{ transform: `translate(${ticketPosition.x}px, ${ticketPosition.y}px) rotate(7deg)` }}
      >
        MOVE<br />THE<br />IDEA
      </button>

      <div className="scrapbook-note" aria-live="polite">
        <span>{artifacts[active].eyebrow}</span>
        <h4>{artifacts[active].label}</h4>
        <p>{artifacts[active].text}</p>
        <small>{artifacts[active].output}</small>
      </div>

      {completed && <div className="scrapbook-complete" role="status">六枚印章集齐 · 策略闭环完成!</div>}
    </div>
  )
}
